import { isLanguageId } from 'app/constants/enums/Language';
import { LanguageId } from 'app/constants/enums/Language/data';
import { isLatinPrefix, LatinPrefix } from 'app/constants/WordPrefix';
import { EntityStringId, makeEntityStringId } from 'app/types/entity';
import { IdentityEntityStringIdCodec } from 'app/types/entity/codecs';
import { GuidFromBase64GuidCodec } from 'app/types/guid';
import { slugify } from 'app/utils/routes';
import { chain, orElse, right } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import * as iots from 'io-ts';

export type EncodedRouteParams<Params> = {
  [K in keyof Params]: string;
};

// Data is always encoded into a string so it can be put into the pathname.
export type RouteCodec<Params> = iots.Type<Params, EncodedRouteParams<Params>, unknown>;

// Masquerade the implementation details of the codecs.
export const makeRouteCodec = <Params>(int: { [K in keyof Params]: ParamCodec<Params[K]> }) => {
  return iots.type(int);
};

// Data is always encoded into a string so it can be put into the pathname.
type ParamCodec<T> = iots.Type<T, string, unknown>;

/**
 * Intended for the purpose of encoding IDs into routing parameters.
 *
 * Note that this is not intended to be used to encode IDs outside of the context of query or
 * route parameters. Bad usage of this includes using it for session storage, sending payloads
 * to the realtime server, reamaze or the backend.
 */
export const EntityStringIdRouteCodec: ParamCodec<EntityStringId> = new iots.Type(
  'EntityStringIdRouteCodec',
  (u: unknown): u is EntityStringId => {
    throw new Error('Behavior undefined.');
  },
  // Allows decoding of base64 guids and regular guids.
  (value, context) => {
    return pipe(
      iots.string.validate(value, context),
      chain((str) => right(decodeURIComponent(str))),
      chain((str) => pipe(
        // Try to decode from a GuidFromBase64GuidCodec and cast to EntityStringId.
        GuidFromBase64GuidCodec.decode(str),
        chain((guid) => right(makeEntityStringId(guid))),
        // If that fails, see if it will decode from an IdentityEntityStringIdCodec.
        orElse(() => IdentityEntityStringIdCodec.validate(str, context)),
      )),
    );
  },
  (value) => {
    return encodeURIComponent(GuidFromBase64GuidCodec.encode(value));
  },
);

export const SlugCodec: ParamCodec<string> = new iots.Type(
  'SlugCodec',
  iots.string.is,
  // Slugs cannot be decoded. All strings are considered valid slugs.
  iots.string.validate,
  slugify,
);

export const LanguageCodec: ParamCodec<LanguageId> = new iots.Type<LanguageId, LanguageId, unknown>(
  'LanguageCodec',
  isLanguageId,
  (v, context) => {
    return isLanguageId(v) ? iots.success(v) : iots.failure(v, context, 'Not a supported language.');
  },
  (a) => a,
);

const isPositiveInteger = (v: unknown): v is number => {
  return iots.Integer.is(v) && v >= 0 && v <= Number.MAX_SAFE_INTEGER;
};

export const PositiveIntegerCodec: ParamCodec<number> = new iots.Type(
  'PositiveIntegerCodec',
  isPositiveInteger,
  (v, context) => {
    try {
      const num = typeof v === 'string' ? parseInt(v, 10) : v;
      return isPositiveInteger(num) ?
        iots.success(num) :
        iots.failure(v, context, 'Not a valid representation of a number.');
    } catch (e) {
      return iots.failure(v, context, e.message);
    }
  },
  (v) => {
    if (v > Number.MAX_SAFE_INTEGER) {
      throw new Error('Number cannot be reliably encoded.');
    }

    if (!isPositiveInteger(v)) {
      throw new Error('Attempting to encode a non positive integer.');
    }

    return v.toString();
  },
);

export const IdentityCodec: ParamCodec<string> = new iots.Type(
  'IdentityCodec',
  iots.string.is,
  (v, context) => {
    if (typeof v === 'string') {
      return iots.success(decodeURIComponent(v));
    } else {
      return iots.failure(v, context, 'Not a string.');
    }
  },
  (v) => {
    return encodeURIComponent(v);
  },
);

export const LatinWordPrefixCodec: ParamCodec<LatinPrefix> = iots.string.pipe(
  new iots.Type<LatinPrefix, string, string>(
    'LatinWordPrefixStringCodec',
    (s: unknown): s is LatinPrefix => isLatinPrefix(s),
    (v, context) => pipe(
      decodeURIComponent(v).toLowerCase(),
      (s) => isLatinPrefix(s) ? iots.success(s) : iots.failure(v, context, 'Does not match a latin character.'),
    ),
    v => encodeURIComponent(v.toLowerCase()),
  ),
  'LatinWordPrefixCodec',
);
